import { Button, Collapse, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import {
  useAfterOneTimePaymentSuccessMutation,
  useProcessOneTimeInvoiceMutation,
} from "src/api/services/invoicesApi";
import { Product, ProductPrice } from "src/api/types";
import AlertWithCopy from "src/components/AlertWithCopy";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useGeneratePaymentLink } from "src/hooks/useGeneratePaymentLink";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import BaseCreateOrderForm from "../../../BaseCreateOrderForm";
import { useExtractForm } from "src/hooks/useExtractForm";
import { useGetDaysSupply } from "src/hooks/useGetDaysSupply";
import { usePatient } from "src/context/PatientProvider";
import { useDeterminePrescriptionType } from "src/hooks/useDeterminePrescriptionType";

interface Props {
  patientStripeId: string;
  patientId: number;
  gender: string | null;
  setIsError: (v: boolean) => void;
  setMessage: (v: string) => void;
}

const CreateOrderNowTab = ({
  patientStripeId,
  patientId,
  gender,
  setIsError,
  setMessage,
}: Props) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<ProductPrice | null>(null);
  const [quantity, setQuantity] = useState<number | undefined>(undefined);
  const [processOneTime] = useProcessOneTimeInvoiceMutation();
  const [afterOneTimePaymentSuccess] = useAfterOneTimePaymentSuccessMutation();
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<boolean>(false);
  const [generatedLink, setGeneratedLink] = useState<string>("");
  const { generatePaymentLink } = useGeneratePaymentLink();
  const error = useSentryLogger("error");
  const form = useExtractForm(selectedPrice, selectedProduct);
  const daysSupply = useGetDaysSupply(selectedProduct, selectedPrice);
  const patientProfile = usePatient((p) => p.profile);
  const prescriptionType = useDeterminePrescriptionType(
    selectedProduct?.id,
    patientId,
  );

  const handleCreateOrder = useCallback(async () => {
    if (selectedProduct == null) return null;

    if (selectedPrice == null) return null;

    setLoading(true);
    try {
      const { data } = await processOneTime({
        patient: {
          id: patientId!,
          stripe_customer_id: patientStripeId,
        },
        body: {
          priceId: selectedPrice.stripe_id,
          paymentMethodId: paymentMethod,
          quantity,
        },
      });

      if (data?.invoice != null) {
        await afterOneTimePaymentSuccess({
          patient: {
            id: patientId!,
            profile: patientProfile,
          },
          body: {
            product_id: selectedProduct.id,
            billing_model: selectedProduct.billing_model,
            product_category_id: selectedProduct.category.id,
            approval_required: selectedProduct.slug === "ec" ? false : true,
            dispense_quantity: quantity?.toString(),
            days_supply: daysSupply,
            metadata: {
              invoice_id: data.invoice.id,
              form: form || undefined,
              prescription_type: prescriptionType,
            },
          },
        }).unwrap();

        setMessage("Order successfully created");
      } else {
        setIsError(true);
        setMessage(
          "Unable to complete order creation, please make sure everything is entered correctly.",
        );
      }
    } catch (err) {
      setIsError(true);
      setMessage(
        "Unable to complete order creation, please make sure everything is entered correctly.",
      );
      error("Error creating one time order", err);
    } finally {
      setLoading(false);
    }
  }, [
    afterOneTimePaymentSuccess,
    daysSupply,
    error,
    form,
    patientId,
    patientProfile,
    patientStripeId,
    paymentMethod,
    prescriptionType,
    processOneTime,
    quantity,
    selectedPrice,
    selectedProduct,
    setIsError,
    setMessage,
  ]);

  const handleGeneratePaymentLink = useCallback(() => {
    if (selectedProduct == null || selectedPrice == null) return;

    try {
      const paymentLink = generatePaymentLink(
        selectedProduct,
        selectedPrice,
        quantity,
      );
      setGeneratedLink(paymentLink);
      setAlert(true);
    } catch (err: unknown) {
      setIsError(true);
      setMessage(
        "Unable to generate payment link, please make sure everything is entered correctly.",
      );
      error("Error generating payment link", err);
    }
  }, [
    error,
    generatePaymentLink,
    quantity,
    selectedPrice,
    selectedProduct,
    setIsError,
    setMessage,
  ]);

  return (
    <Stack gap={2}>
      <BaseCreateOrderForm
        selectedPrice={selectedPrice}
        selectedProduct={selectedProduct}
        setSelectedPrice={setSelectedPrice}
        setSelectedProduct={setSelectedProduct}
        patientId={patientId}
        gender={gender}
        quantity={quantity}
        setQuantity={setQuantity}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        patientStripeId={patientStripeId}
      />
      <Collapse in={!!selectedPrice} unmountOnExit>
        <Button
          variant="secondary"
          fullWidth
          onClick={handleGeneratePaymentLink}
        >
          Generate payment link
        </Button>
      </Collapse>
      {alert ? (
        <AlertWithCopy
          text={`Payment link generated successfully! Please copy and send it to the patient: ${generatedLink}`}
          copyText={generatedLink}
        />
      ) : (
        <LoadingButton
          disabled={!paymentMethod}
          loading={loading}
          onClick={handleCreateOrder}
        >
          Create order
        </LoadingButton>
      )}
    </Stack>
  );
};

export default CreateOrderNowTab;
